/**
 * FinRunoutSettingsDialog — Zahnrad der Finanzierungs-Ausläufer (Jul-2026, User-Wunsch).
 * Kontaktstrecke vor dem Finanzierungsende: Tage je Anschreiben einstellbar,
 * Direktsprung zur E-Mail-Vorlage (Einstellungen), Themen/Schlagwörter mit
 * KI-Ideen (Nachfinanzierung, Upgrades, Ankauf mit Bank-Ablösung u. v. m.).
 */
import { useEffect, useState } from "react";
import { Loader2, Settings, Sparkles, ExternalLink, Play } from "lucide-react";
import { toast } from "sonner";

import api from "../../lib/api";
import { Button } from "../ui/button";
import { Switch } from "../ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";

const STAGES = [
    { key: "financing_runout_first", label: "Erstkontakt", hint: "Anschreiben mit allen Optionen (a–e)" },
    { key: "financing_runout_reminder1", label: "Erinnerung 1", hint: "freundliche Erinnerung" },
    { key: "financing_runout_reminder2", label: "Erinnerung 2", hint: "Nachfrage: erstes Angebot senden?" },
    { key: "financing_runout_reminder3", label: "Erinnerung 3", hint: "dringend — nur noch 2 Wochen" },
    { key: "financing_runout_lastday", label: "Heute endet", hint: "letzter Tag der Finanzierung" },
    { key: "financing_runout_farewell", label: "Verabschiedung", hint: "nach dem Ende (negative Tage = danach)" },
];

export default function FinRunoutSettingsDialog({ open, onClose, settings, onSaved }) {
    const [days, setDays] = useState({});
    const [keywords, setKeywords] = useState("");
    const [autoEmails, setAutoEmails] = useState(false);
    const [busy, setBusy] = useState(false);
    const [runBusy, setRunBusy] = useState(false);
    const [aiBusy, setAiBusy] = useState(false);
    const [ideas, setIdeas] = useState(null);
    // Aug-2026 (User-Wunsch) — Lead-Automatik hierher verlegt (vorher auf der Seite).
    const [autoLeads, setAutoLeads] = useState(false);
    const [windowDays, setWindowDays] = useState(60);
    // Jul-2026 (User-Wunsch) — Öffnungs-Tracking je Stufe (gesendet/geöffnet)
    const [stats, setStats] = useState(null);

    useEffect(() => {
        if (!open || !settings) return;
        setDays({ ...(settings.contact_days || {}) });
        setKeywords(settings.keywords || "");
        setAutoEmails(!!settings.auto_emails);
        setAutoLeads(!!settings.auto_leads);
        setWindowDays(settings.window_days || 60);
        setIdeas(null);
        api.get("/dealer/financing/runout/stats").then((r) => setStats(r.data)).catch(() => {});
    }, [open, settings]);

    const save = async (extra = {}) => {
        setBusy(true);
        try {
            const r = await api.put("/dealer/financing/settings", {
                auto_leads: autoLeads,
                window_days: Math.max(7, Math.min(365, Number(windowDays) || 60)),
                auto_emails: autoEmails,
                contact_days: Object.fromEntries(Object.entries(days).map(([k, v]) => [k, Number(v) || 0])),
                keywords,
                ...extra,
            });
            toast.success("Kontaktstrecke gespeichert.");
            onSaved?.(r.data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Speichern fehlgeschlagen.");
        } finally {
            setBusy(false);
        }
    };

    const runNow = async () => {
        setRunBusy(true);
        try {
            const r = await api.post("/dealer/financing/runout/run");
            toast.success(`Lauf abgeschlossen — ${r.data?.sent ?? 0} E-Mail(s) erzeugt (E-Mail-Freigabe prüfen).`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Lauf fehlgeschlagen.");
        } finally {
            setRunBusy(false);
        }
    };

    const askAi = async () => {
        setAiBusy(true);
        try {
            const r = await api.post("/dealer/financing/ai-ideas", { keywords });
            setIdeas(r.data?.ideas || []);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "KI-Ideen konnten nicht geladen werden.");
        } finally {
            setAiBusy(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
            <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto rounded-sm" data-testid="fin-runout-settings-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 text-[#111827]">
                        <Settings className="w-5 h-5" /> Kontaktstrecke Finanzierungs-Ausläufer
                    </DialogTitle>
                    <DialogDescription className="text-xs text-gray-500">
                        Automatik, Lead-Anlage und Anschreiben-Zeitpunkte für auslaufende Finanzierungen konfigurieren.
                    </DialogDescription>
                </DialogHeader>

                <div className="flex items-center gap-3 bg-gray-50 border border-gray-200 rounded-sm px-3 py-2.5">
                    <Switch checked={autoEmails} onCheckedChange={setAutoEmails} data-testid="fin-runout-auto-toggle" />
                    <div>
                        <div className="text-sm font-bold text-[#111827]">E-Mails automatisch erzeugen</div>
                        <div className="text-[11px] text-gray-500">
                            Täglicher Lauf — jede E-Mail landet zusätzlich in der normalen <b>E-Mail-Freigabe</b>, nichts geht ungeprüft raus.
                        </div>
                    </div>
                    <Button size="sm" variant="outline" onClick={runNow} disabled={runBusy}
                        className="ml-auto h-8 rounded-sm text-xs font-bold shrink-0" data-testid="fin-runout-run-now">
                        {runBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Play className="w-3.5 h-3.5 mr-1" /> Jetzt prüfen & erzeugen</>}
                    </Button>
                </div>

                {/* Aug-2026 (User-Wunsch) — Lead-Automatik hierher ins Zahnrad verlegt,
                    inkl. Anzeige der Verteil-Regel für die automatische Zuweisung. */}
                <div className="bg-gray-50 border border-gray-200 rounded-sm px-3 py-2.5 space-y-2.5" data-testid="fin-auto-leads-block">
                    <div className="flex items-center gap-3">
                        <Switch checked={autoLeads} onCheckedChange={setAutoLeads} data-testid="fin-auto-leads-toggle" />
                        <div className="flex-1 min-w-0">
                            <div className="text-sm font-bold text-[#111827]">Leads automatisch anlegen</div>
                            <div className="text-[11px] text-gray-500">
                                {autoLeads
                                    ? <>Automatik <b className="text-green-700">AN</b> — Leads entstehen automatisch vor Vertragsende.</>
                                    : <>Automatik <b>AUS</b> — Vorgänge werden manuell per „Als Lead übernehmen“ übergeben.</>}
                            </div>
                        </div>
                        <div className="flex items-center gap-1.5 text-xs text-gray-600 shrink-0">
                            <span className="font-bold uppercase tracking-wider text-gray-500">Zeitraum</span>
                            <input type="number" min={7} max={365} value={windowDays}
                                onChange={(e) => setWindowDays(e.target.value)}
                                className="w-16 border border-gray-300 rounded-sm px-2 py-1 font-mono-num focus:border-[#111827] focus:outline-none"
                                data-testid="fin-window-days-input" />
                            <span>Tage vor Ende</span>
                        </div>
                    </div>
                    <div className="text-[11px] text-gray-600 bg-white border border-gray-200 rounded-sm px-2.5 py-2" data-testid="fin-auto-leads-rule">
                        <b>Verteil-Regel:</b>{" "}
                        {settings?.auto_assign_leads_enabled ? (
                            <>Auto-Zuweisung <b className="text-green-700">aktiv</b> — jeder neue Lead geht an einen <b>anwesenden</b> Verkäufer
                            (keine genehmigte Abwesenheit, Standort-/Arbeitszeit-Regeln), darunter an den mit den <b>wenigsten offenen Leads</b>;
                            bei Gleichstand entscheidet die <b>beste Abschlussquote</b>.</>
                        ) : (
                            <>Auto-Zuweisung <b>deaktiviert</b> — neue Leads landen <b>unzugewiesen</b> in „Meine Leads“ und werden manuell verteilt.
                            (Aktivierbar in den Lead-Einstellungen.)</>
                        )}
                    </div>
                </div>

                <div className="space-y-2">
                    <div className="flex items-center justify-between">
                        <div className="text-[10px] font-black uppercase tracking-wider text-gray-400">Anschreiben & Zeitpunkte (Tage vor Ende · negativ = nach Ende)</div>
                        {stats && <span className="text-[10px] font-bold text-violet-600" data-testid="fin-runout-replies-stat">{stats.replies_with_wish} Antworten mit erkanntem Wunsch</span>}
                    </div>
                    {STAGES.map((s) => (
                        <div key={s.key} className="flex items-center gap-3 border border-gray-200 rounded-sm px-3 py-2" data-testid={`fin-runout-stage-${s.key}`}>
                            <div className="min-w-0 flex-1">
                                <div className="text-sm font-bold text-[#111827]">{s.label}</div>
                                <div className="text-[11px] text-gray-500 truncate">{s.hint}</div>
                                {stats?.stages?.[s.key] && (
                                    <div className="text-[10px] font-bold text-gray-400 mt-0.5" data-testid={`fin-runout-stats-${s.key}`}>
                                        {stats.stages[s.key].sent} gesendet · {stats.stages[s.key].opened} geöffnet · {stats.stages[s.key].replied ?? 0} geantwortet
                                    </div>
                                )}
                            </div>
                            <div className="flex items-center gap-1.5 text-xs text-gray-600 shrink-0">
                                <input type="number" min={-60} max={365}
                                    value={days[s.key] ?? ""}
                                    onChange={(e) => setDays((p) => ({ ...p, [s.key]: e.target.value }))}
                                    className="w-16 border border-gray-300 rounded-sm px-2 py-1 font-mono-num focus:border-[#111827] focus:outline-none"
                                    data-testid={`fin-runout-days-${s.key}`} />
                                <span>Tage</span>
                            </div>
                            <button type="button"
                                onClick={() => window.open(`/dashboard?tab=settings&template=${s.key}#settings-email-templates`, "_blank", "noopener")}
                                className="shrink-0 inline-flex items-center gap-1 text-[11px] font-bold text-[#111827] underline decoration-[#FFCC00] decoration-2 hover:text-[#FFCC00]"
                                data-testid={`fin-runout-edit-template-${s.key}`}>
                                Vorlage bearbeiten <ExternalLink className="w-3 h-3" />
                            </button>
                        </div>
                    ))}
                </div>

                <div className="space-y-1.5">
                    <div className="text-[10px] font-black uppercase tracking-wider text-gray-400">Themen / Schlagwörter / Texte (fließen in die KI-Textvorschläge ein)</div>
                    <textarea value={keywords} onChange={(e) => setKeywords(e.target.value.slice(0, 2000))}
                        rows={3} placeholder="z. B. Ballonrate → Nachfinanzierung + Versicherung; Reparatur/Zubehör mitfinanzieren; Upgrade 50→125 ccm inkl. Führerschein; Ankauf mit Bank-Ablösung; Wartungsvertrag …"
                        className="w-full border border-gray-300 rounded-sm px-2.5 py-2 text-sm focus:border-[#111827] focus:outline-none"
                        data-testid="fin-runout-keywords" />
                    <Button size="sm" variant="outline" onClick={askAi} disabled={aiBusy}
                        className="h-8 rounded-sm text-xs font-bold" data-testid="fin-runout-ai-ideas-btn">
                        {aiBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Sparkles className="w-3.5 h-3.5 mr-1 text-[#B8860B]" />}
                        Welche Ideen hat die KI?
                    </Button>
                    {ideas !== null && (
                        <div className="bg-[#FFFBEB] border border-[#FFCC00]/50 rounded-sm px-3 py-2.5 space-y-1" data-testid="fin-runout-ai-ideas">
                            {ideas.length === 0
                                ? <p className="text-xs text-gray-500">Keine Vorschläge erhalten.</p>
                                : ideas.map((i, idx) => <p key={idx} className="text-xs text-[#111827] leading-relaxed">• {i}</p>)}
                        </div>
                    )}
                </div>

                <div className="flex justify-end gap-2 pt-1">
                    <Button variant="outline" onClick={onClose} className="h-9 rounded-sm text-xs font-bold" data-testid="fin-runout-cancel">Schließen</Button>
                    <Button onClick={() => save()} disabled={busy}
                        className="h-9 bg-[#111827] text-white rounded-sm text-xs font-bold" data-testid="fin-runout-save">
                        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Speichern"}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
